import {
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import { fmtShort, fmtC, ChartTooltip } from "./Charts";
import { KpiStatCard } from "@/components/shared/KpiStatCard";

interface FinanceTabProps {
  isLoading: boolean;
  financeStats: {
    totalRevenue: number;
    cashIn: number;
    cashOut: number;
    totalReceivables: number;
    cashFlowTrend: any[];
    prevCashIn?: number;
    prevCashOut?: number;
    prevCashBalance?: number;
    cashBalanceGrowthPct?: number;
  };
  earlyWarnings?: any;
  onDrillDown?: (config: { type: string; title: string; status?: string }) => void;
}

export function FinanceTab({
  isLoading,
  financeStats,
  earlyWarnings,
  onDrillDown,
}: FinanceTabProps) {
  const {
    totalRevenue = 0,
    cashIn = 0,
    cashOut = 0,
    totalReceivables = 0,
    cashFlowTrend = [],
    prevCashIn,
    prevCashOut,
  } = financeStats || {};

  const cashInGrowth =
    prevCashIn !== undefined && prevCashIn > 0
      ? Math.round(((cashIn - prevCashIn) / prevCashIn) * 1000) / 10
      : null;
  const cashOutGrowth =
    prevCashOut !== undefined && prevCashOut > 0
      ? Math.round(((cashOut - prevCashOut) / prevCashOut) * 1000) / 10
      : null;

  const finAlerts = earlyWarnings?.finance || {};
  const overdueDebtorsCount = finAlerts.overdueDebtorsCount ?? 0;
  const creditLimitAlertsCount = finAlerts.creditLimitAlertsCount ?? 0;
  const netCash = finAlerts.netCash ?? (cashIn - cashOut);

  return (
    <div className="space-y-3">
      {/* Cảnh báo vận hành tài chính */}
      <div className="flex flex-wrap items-center gap-2 p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-xl text-xs">
        <span className="font-bold text-blue-700 flex items-center gap-1.5 shrink-0">
          <DollarSign className="h-3.5 w-3.5" />
          Cảnh báo tài chính:
        </span>
        <div className="flex flex-wrap items-center gap-2 text-foreground">
          {overdueDebtorsCount > 0 ? (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "overdue_debtors",
                  title: "Khách nợ quá hạn",
                })
              }
              className="bg-card border-amber-300 text-amber-800 font-semibold cursor-pointer hover:bg-amber-100 transition-colors"
            >
              {`⚠️ ${overdueDebtorsCount} Khách hàng nợ quá hạn`}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-card border-emerald-300 text-emerald-700 font-semibold">
              ✓ Không có nợ quá hạn
            </Badge>
          )}

          {creditLimitAlertsCount > 0 && (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "overdue_debtors",
                  title: "Khách hàng chạm hạn mức tín dụng",
                })
              }
              className="bg-card border-border text-foreground font-semibold cursor-pointer hover:bg-muted transition-colors"
            >
              {`ℹ️ ${creditLimitAlertsCount} Khách chạm hạn mức tín dụng`}
            </Badge>
          )}

          <Badge
            variant="outline"
            className="bg-card border-emerald-200 text-emerald-700 font-semibold"
          >
            {`▲ Dòng tiền ròng: ${netCash >= 0 ? `+${fmtShort(netCash)}` : fmtShort(netCash)}`}
          </Badge>
        </div>
      </div>

      {/* ═══ 4 THẺ KPI TÀI CHÍNH (KpiStatCard & Chuẩn PoP) ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiStatCard
          title="Doanh thu"
          value={totalRevenue}
          formatType="currencyShort"
          colorVariant="blue"
          icon={Activity}
          direction="positive"
          isLoading={isLoading}
          testId="fin-kpi-revenue"
        />

        <KpiStatCard
          title="Tiền thu"
          value={cashIn}
          prevValue={prevCashIn}
          deltaPct={cashInGrowth}
          diff={prevCashIn !== undefined ? cashIn - prevCashIn : undefined}
          formatType="currencyShort"
          colorVariant="emerald"
          icon={ArrowUpRight}
          direction="positive"
          isLoading={isLoading}
          testId="fin-kpi-cash-in"
        />

        <KpiStatCard
          title="Tiền chi"
          value={cashOut}
          prevValue={prevCashOut}
          deltaPct={cashOutGrowth}
          diff={prevCashOut !== undefined ? cashOut - prevCashOut : undefined}
          formatType="currencyShort"
          colorVariant="rose"
          icon={ArrowDownRight}
          direction="inverse"
          isLoading={isLoading}
          testId="fin-kpi-cash-out"
        />

        <KpiStatCard
          title="Phải thu (AR)"
          value={totalReceivables}
          formatType="currencyShort"
          colorVariant="amber"
          icon={DollarSign}
          badgeText={overdueDebtorsCount > 0 ? `${overdueDebtorsCount} quá hạn` : "Đúng hạn"}
          direction="inverse"
          onClick={() =>
            onDrillDown?.({
              type: "overdue_debtors",
              title: "Khách nợ quá hạn",
            })
          }
          isLoading={isLoading}
          testId="fin-kpi-receivables"
        />
      </div>

      {/* Biểu đồ Cân đối Dòng tiền (Tối giản) */}
      <div className="bg-card rounded-xl border border-border p-3.5 shadow-xs">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-primary" /> Dòng tiền thu - chi
          </h3>
        </div>
        {isLoading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : cashFlowTrend?.length === 0 ? (
          <p className="text-xs text-muted-foreground py-8 text-center font-medium">
            Chưa có phát sinh trong kỳ
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={240} minWidth={1} minHeight={1}>
            <ComposedChart data={cashFlowTrend} margin={{ top: 10, right: 12, left: -10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
              <Bar dataKey="cashIn" name="Thực thu" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
              <Bar dataKey="cashOut" name="Thực chi" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Doanh thu"
                stroke="#3b82f6"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#3b82f6", stroke: "white", strokeWidth: 2 }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
